import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from '../hooks/use-toast';
import {
  Upload, Camera, Video, Brain, Activity, Eye, Footprints,
  Target, Dumbbell, ChevronRight, Clock, Trash2, ArrowLeft,
  Play, Square, AlertCircle, CheckCircle2, XCircle, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  footwork: { label: 'Footwork', icon: Footprints, color: 'blue' },
  shuttle_contact: { label: 'Shuttle Contact', icon: Target, color: 'green' },
  racket_position: { label: 'Racket Position', icon: Activity, color: 'purple' },
  eye_direction: { label: 'Eye Direction', icon: Eye, color: 'amber' },
  body_posture: { label: 'Body Posture', icon: Dumbbell, color: 'rose' },
  reaction: { label: 'Reaction', icon: Brain, color: 'cyan' }
};

const ratingBg = (rating) => {
  if (rating === 'good') return 'bg-green-100 text-green-700 border-green-200';
  if (rating === 'needs_work') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const ratingColor = (rating) => {
  if (rating === 'good') return 'text-green-500';
  if (rating === 'needs_work') return 'text-amber-500';
  return 'text-red-500';
};

const ratingIcon = (rating) => {
  if (rating === 'good') return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (rating === 'needs_work') return <AlertCircle className="h-4 w-4 text-amber-500" />;
  return <XCircle className="h-4 w-4 text-red-500" />;
};

const STATUS_MAP = {
  queued: { label: 'Queued', progress: 5, msg: 'Starting analysis...' },
  extracting_frames: { label: 'Extracting', progress: 20, msg: 'Extracting frames from video...' },
  analyzing: { label: 'Analyzing', progress: 55, msg: 'AI analyzing frames in parallel...' },
  generating_drills: { label: 'Drills', progress: 85, msg: 'Generating personalized drills...' },
  complete: { label: 'Done', progress: 100, msg: 'Analysis complete!' },
  error: { label: 'Error', progress: 0, msg: 'Analysis failed' }
};

// --- Video Analysis Tab ---
const VideoAnalysis = ({ token }) => {
  const [uploading, setUploading] = useState(false);
  const [polling, setPolling] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [sessions, setSessions] = useState([]);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { fetchSessions(); }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/ai-coach/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data);
    } catch (e) { console.error(e); }
  };

  const startPolling = (sessionId) => {
    setPolling(sessionId);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/ai-coach/sessions/${sessionId}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPollStatus(res.data);

        if (res.data.status === 'complete') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPolling(null);
          // Fetch full session
          const full = await axios.get(`${API}/ai-coach/sessions/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setResult(full.data);
          fetchSessions();
          toast({ title: 'Analysis complete', description: `Analyzed ${res.data.frames_analyzed} key frames.` });
        } else if (res.data.status === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPolling(null);
          toast({ title: 'Error', description: res.data.error || 'Analysis failed', variant: 'destructive' });
        }
      } catch (e) {
        console.error('Poll failed:', e);
      }
    }, 3000);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Please upload a video file.', variant: 'destructive' });
      return;
    }

    setUploading(true);
    setPollStatus(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/ai-coach/analyze-video`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 600000
      });
      toast({ title: 'Upload complete', description: `Video accepted (${res.data.video_duration_sec}s). Analysis running...` });
      startPolling(res.data.id);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed. Try again.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      setPolling(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const viewSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/ai-coach/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'complete') {
        setResult(res.data);
      } else if (['queued', 'extracting_frames', 'analyzing', 'generating_drills'].includes(res.data.status)) {
        startPolling(sessionId);
      } else {
        toast({ title: 'Session failed', description: res.data.error || 'This analysis did not complete.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load session', variant: 'destructive' });
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/ai-coach/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(s => s.filter(x => x.id !== sessionId));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  if (result) {
    return <AnalysisReport data={result} onBack={() => { setResult(null); }} />;
  }

  const statusInfo = pollStatus ? STATUS_MAP[pollStatus.status] || STATUS_MAP.queued : null;

  return (
    <div className="space-y-4">
      {/* Upload / Progress Area */}
      <Card className="border-2 border-dashed border-blue-300 bg-blue-50/30" data-testid="video-upload-card">
        <CardContent className="py-6 text-center">
          {uploading || polling ? (
            <div className="space-y-3">
              <Loader2 className="h-10 w-10 mx-auto text-blue-500 animate-spin" />
              <p className="font-medium text-blue-700">{statusInfo?.msg || 'Uploading video...'}</p>
              <Progress value={statusInfo?.progress || 10} className="max-w-xs mx-auto" />
              {pollStatus && (
                <div className="text-xs text-gray-500 space-y-0.5">
                  {pollStatus.video_duration_sec > 0 && <p>Video: {Math.round(pollStatus.video_duration_sec)}s</p>}
                  {pollStatus.total_frames_extracted > 0 && <p>Frames extracted: {pollStatus.total_frames_extracted}</p>}
                  {pollStatus.frames_analyzed > 0 && <p>Frames analyzed: {pollStatus.frames_analyzed}</p>}
                </div>
              )}
              <p className="text-xs text-gray-400">Parallel analysis - 4-5x faster than sequential</p>
            </div>
          ) : (
            <>
              <Upload className="h-10 w-10 mx-auto text-blue-400 mb-3" />
              <p className="font-semibold text-gray-700 mb-1">Upload Badminton Video</p>
              <p className="text-sm text-gray-500 mb-1">Supports videos up to 1 hour</p>
              <p className="text-xs text-gray-400 mb-4">AI extracts hundreds of frames and analyzes in parallel</p>
              <input ref={fileInputRef} type="file" accept="video/*" onChange={handleUpload} className="hidden" data-testid="video-file-input" />
              <Button onClick={() => fileInputRef.current?.click()} data-testid="upload-video-btn">
                <Video className="h-4 w-4 mr-2" /> Select Video
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Past Sessions */}
      {sessions.length > 0 && (
        <Card data-testid="past-sessions-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Past Analyses</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {sessions.map(s => (
                <div key={s.id} className="p-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <button onClick={() => viewSession(s.id)} className="flex-1 text-left flex items-center gap-3" data-testid={`session-${s.id}`}>
                    <Brain className="h-5 w-5 text-blue-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.filename || 'Video Analysis'}</p>
                      <p className="text-xs text-gray-500">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {new Date(s.created_at).toLocaleDateString()}
                        {s.video_duration_sec > 0 && ` · ${Math.round(s.video_duration_sec)}s`}
                        {s.frames_analyzed > 0 && ` · ${s.frames_analyzed} frames`}
                      </p>
                      {s.status && s.status !== 'complete' && (
                        <Badge variant="outline" className="text-[10px] mt-0.5">{s.status}</Badge>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewSession(s.id)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteSession(s.id)} data-testid={`delete-session-${s.id}`}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Analysis Report ---
const AnalysisReport = ({ data, onBack }) => {
  const summary = data.weakness_summary || {};
  const drills = data.drills || {};
  const drillList = drills.drills || [];
  const priorityAreas = drills.priority_areas || [];
  const overallAssessment = drills.overall_assessment || '';

  return (
    <div className="space-y-4" data-testid="analysis-report">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-2" data-testid="back-from-report">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      {/* Info Bar */}
      {data.video_duration_sec > 0 && (
        <div className="flex gap-2 flex-wrap text-xs text-gray-500">
          <Badge variant="outline">{Math.round(data.video_duration_sec)}s video</Badge>
          <Badge variant="outline">{data.total_frames_extracted} frames extracted</Badge>
          <Badge variant="outline">{data.frames_analyzed} frames analyzed</Badge>
        </div>
      )}

      {/* Overall Assessment */}
      {overallAssessment && (
        <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Brain className="h-6 w-6 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">Overall Assessment</p>
                <p className="text-sm text-blue-100">{overallAssessment}</p>
              </div>
            </div>
            {priorityAreas.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {priorityAreas.map((area, i) => (
                  <Badge key={i} variant="secondary" className="bg-white/20 text-white border-0 text-xs">{area}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Scores */}
      <Card data-testid="category-scores-card">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">Technique Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(summary).map(([key, val]) => {
            const meta = CATEGORY_META[key];
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <div key={key} className="flex items-center gap-3" data-testid={`category-${key}`}>
                <Icon className={`h-5 w-5 text-${meta.color}-500 shrink-0`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{meta.label}</span>
                    <Badge variant="outline" className={`text-xs ${ratingBg(val.overall_rating)}`}>
                      {val.overall_rating === 'good' ? 'Good' : val.overall_rating === 'needs_work' ? 'Needs Work' : 'Critical'}
                    </Badge>
                  </div>
                  <Progress value={val.score || 0} className="h-2" />
                  {val.improvements?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{val.improvements[0]}</p>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Recommended Drills */}
      {drillList.length > 0 && (
        <Card data-testid="drills-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Dumbbell className="h-4 w-4 text-orange-500" /> Recommended Drills
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {drillList.map((drill, i) => (
              <div key={i} className="p-3 rounded-lg bg-gray-50 border" data-testid={`drill-${i}`}>
                <div className="flex items-start justify-between mb-1">
                  <p className="font-medium text-sm">{drill.name}</p>
                  <Badge variant="outline" className="text-xs shrink-0 ml-2">{drill.difficulty}</Badge>
                </div>
                <p className="text-xs text-gray-600 mb-2">{drill.description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Target className="h-3 w-3" /> {drill.targets}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {drill.duration}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Frame Details */}
      {data.frame_analyses?.length > 0 && (
        <Card data-testid="frame-details-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Frame-by-Frame Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.frame_analyses.map((fa, i) => (
              <details key={i} className="border rounded-lg">
                <summary className="p-2 cursor-pointer text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                  Frame {fa.frame_number} / {fa.total_frames}
                  <span className="ml-auto flex gap-1">
                    {Object.values(fa.analysis?.frame_analysis || {}).map((v, j) => (
                      <span key={j}>{ratingIcon(v.rating)}</span>
                    ))}
                  </span>
                </summary>
                <div className="p-3 border-t space-y-2">
                  {fa.analysis?.overall_notes && (
                    <p className="text-xs text-gray-600 italic">{fa.analysis.overall_notes}</p>
                  )}
                  {Object.entries(fa.analysis?.frame_analysis || {}).map(([cat, val]) => {
                    const meta = CATEGORY_META[cat];
                    return (
                      <div key={cat} className="text-xs">
                        <span className="font-medium">{meta?.label || cat}:</span>
                        <span className={`ml-1 ${ratingColor(val.rating)}`}>{val.observation}</span>
                        {val.improvement && <span className="text-gray-400 ml-1">- {val.improvement}</span>}
                      </div>
                    );
                  })}
                </div>
              </details>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Live Coach Tab ---
const LiveCoach = ({ token }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setIsActive(true);
      intervalRef.current = setInterval(captureAndAnalyze, 3500);
    } catch (err) {
      setCameraError('Camera access denied. Please allow camera permissions.');
    }
  };

  const stopCamera = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setIsActive(false);
    setFeedback(null);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const captureAndAnalyze = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || analyzing) return;
    setAnalyzing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = 640;
    canvas.height = 480;
    canvas.getContext('2d').drawImage(video, 0, 0, 640, 480);
    const base64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];

    try {
      const res = await axios.post(`${API}/ai-coach/live-analyze`, { frame_base64: base64 }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setFeedback(res.data);
      setFeedbackHistory(prev => [res.data, ...prev].slice(0, 20));
    } catch (err) {
      console.error('Live analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  }, [token, analyzing]);

  return (
    <div className="space-y-4">
      <Card data-testid="live-coach-card">
        <CardContent className="p-0 relative overflow-hidden rounded-lg">
          {!isActive ? (
            <div className="py-16 text-center bg-gray-900 rounded-lg">
              <Camera className="h-12 w-12 mx-auto text-gray-500 mb-3" />
              <p className="text-gray-400 mb-1 font-medium">Live AI Coach</p>
              <p className="text-gray-500 text-sm mb-4">Real-time feedback while doing drills</p>
              {cameraError && <p className="text-red-400 text-sm mb-3">{cameraError}</p>}
              <Button onClick={startCamera} className="bg-green-600 hover:bg-green-700" data-testid="start-camera-btn">
                <Play className="h-4 w-4 mr-2" /> Start Live Coach
              </Button>
            </div>
          ) : (
            <div className="relative">
              <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-black" data-testid="live-video-feed" />
              <canvas ref={canvasRef} className="hidden" />
              {feedback && (
                <div className={`absolute bottom-0 left-0 right-0 p-3 ${
                  feedback.severity === 'good' ? 'bg-green-900/80' :
                  feedback.severity === 'adjust' ? 'bg-amber-900/80' : 'bg-red-900/80'
                } backdrop-blur-sm`} data-testid="live-feedback-overlay">
                  <p className="text-white font-semibold text-sm">{feedback.primary_feedback}</p>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline" className="text-white border-white/30 text-xs">{feedback.area}</Badge>
                    <span className="text-white/70 text-xs">{feedback.quick_tip}</span>
                  </div>
                </div>
              )}
              {analyzing && <div className="absolute top-3 right-3"><div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" /></div>}
              <div className="absolute top-3 left-3">
                <Button size="sm" variant="destructive" onClick={stopCamera} data-testid="stop-camera-btn">
                  <Square className="h-3 w-3 mr-1" /> Stop
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {feedbackHistory.length > 0 && (
        <Card data-testid="feedback-history-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Session Feedback Log</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-60 overflow-y-auto">
            {feedbackHistory.map((fb, i) => (
              <div key={i} className={`p-2 rounded text-sm flex items-start gap-2 ${
                fb.severity === 'good' ? 'bg-green-50' : fb.severity === 'adjust' ? 'bg-amber-50' : 'bg-red-50'
              }`}>
                {ratingIcon(fb.severity === 'good' ? 'good' : fb.severity === 'adjust' ? 'needs_work' : 'critical')}
                <div>
                  <p className="text-xs font-medium">{fb.primary_feedback}</p>
                  <p className="text-xs text-gray-500">{fb.area} · {fb.quick_tip}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Main AICoach Component ---
const AICoach = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('video');

  return (
    <div className="space-y-4" data-testid="ai-coach-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
          <Brain className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="font-bold text-lg">AI Badminton Coach</h2>
          <p className="text-xs text-gray-500">Claude Sonnet 4.5 · Parallel Analysis</p>
        </div>
      </div>

      <div className="flex gap-2" data-testid="coach-tabs">
        <Button variant={tab === 'video' ? 'default' : 'outline'} size="sm" onClick={() => setTab('video')} data-testid="tab-video">
          <Video className="h-4 w-4 mr-1" /> Video Analysis
        </Button>
        <Button variant={tab === 'live' ? 'default' : 'outline'} size="sm" onClick={() => setTab('live')} data-testid="tab-live">
          <Camera className="h-4 w-4 mr-1" /> Live Coach
        </Button>
      </div>

      {tab === 'video' ? <VideoAnalysis token={token} /> : <LiveCoach token={token} />}
    </div>
  );
};

export default AICoach;
